import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ComposedChart, Area, Line, BarChart, Bar, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import MapPlaceholder from "../components/ui/MapPlaceholder";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, STATUS_KEY, statusHex, statusClasses, thresholdStatus, normalizeKey } from "../data/statusColors";
import { crowdTrendData, routeSaturation, crowdFlowByZone, crowdHeatmapHours } from "../data/mockData";
import {
  TrendingUp, TrendingDown, AlertTriangle, Activity, Users, MapPin,
  Wind, Zap, Clock, CheckCircle, Eye, Navigation, ChevronDown, ChevronUp,
  Waves, ArrowUp, ArrowDown, Minus, Radio
} from "lucide-react";

// ── inline data ───────────────────────────────────────────────────
const GATE_FLOW = [
  { gate:"Gate 1 — Ramkund Main",   entry:4820, exit:3240, net:+1580, status:"critical", capacity:5000 },
  { gate:"Gate 2 — Ramkund Alt",    entry:2140, exit:2680, net:-540,  status:"high",     capacity:3000 },
  { gate:"Gate 3 — Panchvati North",entry:3620, exit:2940, net:+680,  status:"high",     capacity:4000 },
  { gate:"Gate 4 — Panchvati South",entry:1880, exit:2120, net:-240,  status:"moderate", capacity:3000 },
  { gate:"Gate 5 — Trimbak Rd",     entry:2940, exit:2180, net:+760,  status:"high",     capacity:3500 },
  { gate:"Gate 6 — Nashik Rd Stn",  entry:1640, exit:1420, net:+220,  status:"moderate", capacity:2500 },
  { gate:"Gate 7 — Kalaram Templ",  entry:1280, exit:1380, net:-100,  status:"normal",   capacity:2000 },
  { gate:"Gate 8 — Gangapur Camp",  entry:860,  exit:920,  net:-60,   status:"normal",   capacity:1500 },
];

const FOUR_HOUR_FORECAST = [
  { time:"14:00", p25:620000, p50:680000, p75:720000, risk:5.8 },
  { time:"14:30", p25:640000, p50:700000, p75:748000, risk:6.1 },
  { time:"15:00", p25:670000, p50:730000, p75:782000, risk:6.6 },
  { time:"15:30", p25:695000, p50:755000, p75:808000, risk:6.9 },
  { time:"16:00", p25:710000, p50:775000, p75:830000, risk:7.2 },
  { time:"16:30", p25:695000, p50:758000, p75:812000, risk:7.0 },
  { time:"17:00", p25:660000, p50:720000, p75:775000, risk:6.5 },
  { time:"17:30", p25:620000, p50:675000, p75:726000, risk:6.0 },
  { time:"18:00", p25:570000, p50:622000, p75:670000, risk:5.4 },
];

const BOTTLENECKS = [
  { id:"BN-01", location:"Ramkund Entry 2 — archway", eta:"Now",   density:9.2, velocity:0.4, risk:9.1, action:"Active diversion in progress",     status:"CRITICAL" },
  { id:"BN-02", location:"Godavari Bridge South",      eta:"T+18m", density:8.1, velocity:0.6, risk:7.8, action:"Police deployment recommended",    status:"HIGH"     },
  { id:"BN-03", location:"Panchvati Ghat Steps",       eta:"T+35m", density:7.4, velocity:0.7, risk:6.9, action:"Crowd pacing announcements active", status:"HIGH"     },
  { id:"BN-04", location:"Kalaram Temple Queue",        eta:"T+52m", density:6.8, velocity:0.9, risk:5.8, action:"Monitoring — advisory issued",      status:"MODERATE" },
  { id:"BN-05", location:"Trimbak Bus Stand Exit",     eta:"T+1h",  density:6.1, velocity:1.1, risk:4.9, action:"No action required",               status:"WATCH"    },
];

const RISK_FACTORS = [
  { factor:"Crowd Density",   value:82, unit:"persons/100m²",  icon:Users,     color:"#EF4444" },
  { factor:"Flow Velocity",   value:38, unit:"m/min avg",      icon:Wind,      color:"#F97316" },
  { factor:"Pressure Index",  value:74, unit:"composite",      icon:Waves,     color:"#F59E0B" },
  { factor:"Turbulence",      value:56, unit:"movement chaos", icon:Activity,  color:"#7C3AED" },
  { factor:"Bottleneck Count",value:5,  unit:"active points",  icon:Zap,       color:"#EF4444", raw:true },
  { factor:"Exit Deficit",    value:3,  unit:"gates > inflow", icon:Navigation,color:"#2563EB", raw:true },
];

const ZONE_RADAR = [
  { zone:"Ramkund",  density:92, velocity:38, pressure:88, turbulence:72, exitFlow:64 },
  { zone:"Panchvati",density:81, velocity:52, pressure:76, turbulence:58, exitFlow:72 },
  { zone:"Trimbak",  density:76, velocity:58, pressure:68, turbulence:48, exitFlow:78 },
  { zone:"Nashik Rd",density:68, velocity:64, pressure:58, turbulence:38, exitFlow:82 },
  { zone:"Kalaram",  density:58, velocity:72, pressure:48, turbulence:28, exitFlow:88 },
  { zone:"Gangapur", density:42, velocity:84, pressure:32, turbulence:18, exitFlow:92 },
];

const RADAR_AXES = [
  { zone:"Ramkund",  density:92, velocity:38, pressure:88, turbulence:72, exitFlow:64 },
];

const HISTORICAL = [
  { event:"Nashik Kumbh 2015 — Snan 1", peak:1100000, incidents:48, risk:8.4 },
  { event:"Ujjain Simhastha 2016",       peak:950000,  incidents:32, risk:7.8 },
  { event:"Nashik Kumbh 2015 — Snan 2", peak:1050000, incidents:38, risk:8.1 },
  { event:"Trimbak Snan 2015",           peak:820000,  incidents:22, risk:6.9 },
  { event:"Today — Simhastha 2027",      peak:810000,  incidents:18, risk:5.8 },
];

const AI_INTERVENTIONS = [
  { priority:"P1", action:"Divert Gate 2 overflow → Gate 4 via Godavari Bridge Road",   zone:"Ramkund",  confidence:96, status:"Active",   dept:"Police"     },
  { priority:"P1", action:"Open Camp Delta — Nashik Road shelter overflow",               zone:"Nashik Rd",confidence:94, status:"In Progress",dept:"Revenue"  },
  { priority:"P2", action:"Deploy 2 MSRTC buses — Trimbak Road → Nashik Road Stn",      zone:"Trimbak",  confidence:91, status:"Pending",  dept:"Transport"  },
  { priority:"P2", action:"Activate ORS stations at Panchvati Ghat Steps",               zone:"Panchvati",confidence:89, status:"Active",   dept:"Health"     },
  { priority:"P3", action:"Broadcast Marathi crowd guidance — Ramkund east entrance",    zone:"Ramkund",  confidence:88, status:"Active",   dept:"Comms"      },
  { priority:"P3", action:"Move medical team to Godavari Bridge South pre-position",     zone:"Panchvati",confidence:85, status:"Pending",  dept:"Health"     },
];

const VELOCITY_DATA = [
  { time:"10:00", ramkund:0.9, panchvati:1.2, trimbak:1.4, nashikRd:1.8 },
  { time:"10:30", ramkund:0.8, panchvati:1.1, trimbak:1.3, nashikRd:1.7 },
  { time:"11:00", ramkund:0.6, panchvati:0.9, trimbak:1.1, nashikRd:1.6 },
  { time:"11:30", ramkund:0.5, panchvati:0.8, trimbak:1.0, nashikRd:1.5 },
  { time:"12:00", ramkund:0.4, panchvati:0.7, trimbak:0.9, nashikRd:1.4 },
  { time:"12:30", ramkund:0.4, panchvati:0.7, trimbak:0.9, nashikRd:1.5 },
  { time:"13:00", ramkund:0.5, panchvati:0.8, trimbak:1.0, nashikRd:1.6 },
  { time:"13:30", ramkund:0.4, panchvati:0.7, trimbak:0.9, nashikRd:1.4 },
  { time:"14:00", ramkund:0.4, panchvati:0.6, trimbak:0.9, nashikRd:1.3 },
];

const CROWD_COMPOSITION = [
  { name:"Senior Citizens (60+)", value:18, color:"#7C3AED" },
  { name:"Families w/ children",  value:34, color:"#2563EB" },
  { name:"Adult pilgrims",        value:38, color:"#10B981" },
  { name:"Differently abled",     value:4,  color:"#F59E0B" },
  { name:"VIP / Media",           value:6,  color:"#EF4444" },
];

// ── helpers ───────────────────────────────────────────────────────
// Zone/gate/bottleneck/route status vocabulary (critical/high/moderate/normal,
// plus uppercase CRITICAL/HIGH/MODERATE/WATCH variants) all collapse onto the
// shared STATUS_KEY buckets so a "critical" zone reads identically to a
// "critical" badge everywhere else in the app.
const zoneBucket = r => {
  const k = normalizeKey(r);
  return k === "critical" ? STATUS_KEY.critical :
         k === "high"     ? STATUS_KEY.serious  :
         k === "moderate" || k === "watch" ? STATUS_KEY.warning : STATUS_KEY.good;
};
const riskColor = r => statusHex(zoneBucket(r));
const riskBg = r => statusClasses(zoneBucket(r));

const trendIcon = t =>
  t === "rising"  ? <ArrowUp size={11} className="text-red-500" /> :
  t === "falling" ? <ArrowDown size={11} className="text-emerald-500" /> :
  <Minus size={11} className="text-amber-500" />;

// ── component ─────────────────────────────────────────────────────
export default function CrowdIntelligence() {
  const t = useTranslate();
  const [activeZone, setActiveZone]     = useState(null);
  const [expandedBN, setExpandedBN]     = useState(null);
  const [radarZone, setRadarZone]       = useState(0);

  const selectedZone = crowdFlowByZone.find(z => z.zone === activeZone);

  return (
    <div className="space-y-8">
      <SectionHeader icon="Users" title="Predictive Crowd Risk Engine"
        subtitle="360° crowd intelligence · density · velocity · pressure · bottleneck AI · gate flow · 4-hour forecast · stampede risk · intervention recommendations"
        badge="RISK: 5.8/10"
      />

      {/* KPIs — 6 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard title="Current Pilgrims" value="6,84,210" icon="Users"          color="blue"   index={0} live />
        <MetricCard title="Crowd Risk Index" value="5.8 / 10" icon="AlertTriangle"  color="amber"  index={1} />
        <MetricCard title="Peak Estimate"    value="8,10,000" icon="TrendingUp"     color="red"    index={2} />
        <MetricCard title="Critical Zones"   value="2"        icon="MapPin"         color="red"    index={3} live />
        <MetricCard title="Bottlenecks"      value="5"        icon="Zap"            color="amber"  index={4} />
        <MetricCard title="Avg Density"      value="7.2 /m²"  icon="Activity"      color="purple" index={5} />
      </div>

      {/* Risk Score Banner */}
      <motion.div initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-yellow-300 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("LIVE CROWD RISK ASSESSMENT — 14:33 IST")}</span>
            </div>
            <div className="flex items-end gap-3">
              <div className="text-6xl font-black text-slate-900">5.8</div>
              <div>
                <div className="text-xs text-slate-500">{t("/ 10.0 Crowd Risk Index")}</div>
                <div className="text-[10px] text-slate-900 font-bold mt-0.5">{t("ELEVATED — Pre-peak conditions · Ramkund critical")}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { label:"Density Risk",    val:"HIGH",   sub:"9.2/m² at Ramkund" },
              { label:"Velocity",        val:"LOW",    sub:"0.4 m/min — slow flow" },
              { label:"Pressure",        val:"HIGH",   sub:"Surge from Entry 2" },
              { label:"Turbulence",      val:"MED",    sub:"Movement chaos 56%" },
              { label:"Exit Coverage",   val:"MED",    sub:"3 gates net +ve" },
              { label:"Response Ready",  val:"GOOD",   sub:"All units deployed" },
            ].map((s, i) => {
              const c = googleColor(i);
              return (
                <div key={s.label}
                  className="rounded-xl px-3 py-2 min-w-[110px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-xs font-black" style={{ color:c.text }}>{t(s.val)}</div>
                  <div className="text-[9px] mt-0.5" style={{ color:c.text }}>{t(s.label)}</div>
                  <div className="text-[9px]" style={{ color:c.text }}>{t(s.sub)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Stampede Risk Factors */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-3">{t("Stampede Risk Factor Gauges")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {RISK_FACTORS.map((f, i) => {
            const Icon = f.icon;
            const pct = f.raw ? Math.min(f.value * 10, 100) : f.value;
            return (
              <motion.div key={f.factor}
                initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                className="card p-4 text-center"
              >
                <div className="flex items-center justify-center mb-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center"
                    style={{ background:`${f.color}18` }}>
                    <Icon size={16} style={{ color: f.color }} />
                  </div>
                </div>
                <div className="text-xl font-black mb-0.5" style={{ color: f.color }}>
                  {f.raw ? f.value : `${f.value}%`}
                </div>
                <div className="text-[10px] font-bold text-slate-700 mb-1.5">{t(f.factor)}</div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-1.5 rounded-full" style={{ background: f.color }}
                    initial={{ width:0 }} animate={{ width:`${pct}%` }} transition={{ duration:0.9, delay: i * 0.07 }} />
                </div>
                <div className="text-[9px] text-slate-400 mt-1">{t(f.unit)}</div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Map + Zone panel */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3">
          <ChartCard title="Live Crowd Density Map" subtitle="Real-time zone load · heatmap overlay · bottleneck markers" live>
            <MapPlaceholder height={400} showHeatmap />
            {/* Map legend */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-[10px]">
              {[
                { color:"bg-red-400",    label:"Critical >85%" },
                { color:"bg-orange-400", label:"High 70–85%" },
                { color:"bg-amber-400",  label:"Moderate 50–70%" },
                { color:"bg-emerald-400",label:"Normal <50%" },
              ].map(l => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <span className={`w-3 h-3 rounded-full ${l.color}`} />
                  <span className="text-slate-600">{t(l.label)}</span>
                </div>
              ))}
              <div className="ml-auto flex items-center gap-1 text-slate-500">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
                {t("Updated 14:33")}
              </div>
            </div>
          </ChartCard>
        </div>

        <div className="xl:col-span-2 flex flex-col gap-4">
          {/* Zone cards */}
          <div className="card p-4">
            <h3 className="text-sm font-black text-slate-900 mb-3">{t("Zone Status")}</h3>
            <div className="space-y-2">
              {crowdFlowByZone.map((z, i) => (
                <motion.div key={z.zone}
                  initial={{ opacity:0, x:8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${
                    activeZone === z.zone ? "border-blue-300 bg-blue-50 shadow-card" :
                    z.risk === "critical" ? "border-red-200 bg-red-50/30 hover:border-red-300" :
                    "border-slate-200 hover:border-blue-200"
                  }`}
                  onClick={() => setActiveZone(activeZone === z.zone ? null : z.zone)}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-800">{t(z.zone)}</span>
                    <div className="flex items-center gap-1.5">
                      {trendIcon(z.trend)}
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${riskBg(z.risk)}`}>
                        {t(z.risk.toUpperCase())}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className="h-2 rounded-full" style={{ background: riskColor(z.risk) }}
                      initial={{ width:0 }} animate={{ width:`${Math.round(z.current/z.capacity*100)}%` }}
                      transition={{ duration:0.8, delay: i * 0.06 }} />
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[9px] text-slate-500">
                    <span>{z.current.toLocaleString()} {t("pilgrims")}</span>
                    <span className="font-bold">{z.density}/m² · {Math.round(z.current/z.capacity*100)}%</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Zone detail on click */}
          <AnimatePresence>
            {selectedZone && (
              <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:6 }}
                className={`card p-4 border-2 ${STATUS[zoneBucket(selectedZone.risk)].border}`}
              >
                <h4 className="text-xs font-black text-slate-900 mb-3">{t(selectedZone.zone)} — {t("Detail")}</h4>
                <div className="grid grid-cols-2 gap-2 text-center text-xs">
                  {[
                    { label:"Pilgrims", val:selectedZone.current.toLocaleString(), color:"text-blue-700" },
                    { label:"Density",  val:`${selectedZone.density}/m²`,          color:"text-red-600"  },
                    { label:"Capacity", val:`${Math.round(selectedZone.current/selectedZone.capacity*100)}%`, color: STATUS[zoneBucket(selectedZone.risk)].text },
                    { label:"Trend",    val:selectedZone.trend,                    color: selectedZone.trend === "rising" ? STATUS.critical.text : selectedZone.trend === "falling" ? STATUS.good.text : STATUS.warning.text },
                  ].map(d => (
                    <div key={d.label} className="bg-slate-50 rounded-xl p-2">
                      <div className={`text-base font-black ${d.color}`}>{t(d.val)}</div>
                      <div className="text-[9px] text-slate-500">{t(d.label)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[10px] text-slate-500 text-center">{t("Click zone again to collapse")}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Crowd Flow Actual vs Predicted */}
      <ChartCard title="Crowd Flow — Actual vs Predicted (Today)"
        subtitle="AI 95% prediction band · risk index overlay · full day Simhastha forecast" live>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={crowdTrendData} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <defs>
              <linearGradient id="ciActGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="ciPredGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor(6)} stopOpacity={0.15} />
                <stop offset="100%" stopColor={chartColor(6)} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="time" tick={axisTick} />
            <YAxis yAxisId="left" tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
            <YAxis yAxisId="right" orientation="right" domain={[0,10]} tick={axisTick} />
            <Tooltip content={<ChartTooltip formatter={(v, n) => [n === t("Risk Index") ? `${v}/10` : v?.toLocaleString(), n]} />} />
            <Legend wrapperStyle={legendStyle} />
            <Area yAxisId="left" type="monotone" dataKey="actual"    fill="url(#ciActGrad)"  stroke={chartColor(0)} strokeWidth={2.5} name={t("Actual")} />
            <Area yAxisId="left" type="monotone" dataKey="predicted" fill="url(#ciPredGrad)" stroke={chartColor(6)} strokeWidth={2} strokeDasharray="5 3" name={t("AI Predicted")} />
            <Line yAxisId="right" type="monotone" dataKey="risk" stroke={statusHex("critical")} strokeWidth={2.5} dot={{ r:3 }} name={t("Risk Index")} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-100 text-center text-xs">
          {[
            { label:"Current",  val:"6,84,210",   color:"text-blue-700" },
            { label:"Peak Est.", val:"8,10,000",  color:"text-red-600"  },
            { label:"Peak Time", val:"15:00 IST", color:"text-amber-600"},
            { label:"Peak Risk", val:"7.8/10",    color:"text-red-600"  },
          ].map(s => (
            <div key={s.label}>
              <div className={`text-base font-black ${s.color}`}>{s.val}</div>
              <div className="text-[10px] text-slate-400">{t(s.label)}</div>
            </div>
          ))}
        </div>
      </ChartCard>

      {/* 4-Hour Prediction Window */}
      <ChartCard title="4-Hour Predictive Window — P25 / P50 / P75 Forecast"
        subtitle="Probabilistic crowd count bands with 30-min intervals · AI Monte Carlo simulation" live>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={FOUR_HOUR_FORECAST} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <defs>
              <linearGradient id="p75Grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor(1)} stopOpacity={0.15} />
                <stop offset="100%" stopColor={chartColor(1)} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="time" tick={axisTick} />
            <YAxis yAxisId="left" tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
            <YAxis yAxisId="right" orientation="right" domain={[4,10]} tick={axisTick} />
            <Tooltip content={<ChartTooltip formatter={(v, n) => [n.startsWith("p") ? v.toLocaleString() : `${v}/10`, n]} />} />
            <Legend wrapperStyle={legendStyle} />
            <Area yAxisId="left" type="monotone" dataKey="p75" fill="url(#p75Grad)" stroke={chartColor(1)} strokeWidth={1.5} strokeDasharray="4 2" name={t("P75 (high)")} />
            <Area yAxisId="left" type="monotone" dataKey="p50" fill={chartColor(0)} fillOpacity={0.15} stroke={chartColor(0)} strokeWidth={2.5} name={t("P50 (median)")} />
            <Area yAxisId="left" type="monotone" dataKey="p25" fill={chartColor(2)} fillOpacity={0.15} stroke={chartColor(2)} strokeWidth={1.5} strokeDasharray="4 2" name={t("P25 (low)")} />
            <Line yAxisId="right" type="monotone" dataKey="risk" stroke={statusHex("critical")} strokeWidth={2} dot={{ r:3 }} name={t("Risk Index")} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-2 p-3 rounded-xl bg-yellow-50 border border-yellow-300">
          <p className="text-xs text-yellow-900 font-semibold">
            <AlertTriangle size={11} className="inline mr-1" />
            {t("P75 scenario at 16:00 reaches 8,30,000 pilgrims — Risk Index 7.2. Pre-positioning of additional 200 police recommended by 15:30.")}
          </p>
        </div>
      </ChartCard>

      {/* Bottleneck Predictions */}
      <ChartCard title="AI Bottleneck Prediction Engine"
        subtitle="Predicted congestion points, ETA, density, velocity and recommended action">
        <div className="space-y-2">
          {BOTTLENECKS.map((b, i) => (
            <motion.div key={b.id}
              initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
              className={`rounded-xl border transition-all ${
                b.status === "CRITICAL" ? `${STATUS.critical.border} bg-red-50/40` :
                b.status === "HIGH"     ? `${STATUS.serious.border} bg-orange-50/30` :
                b.status === "MODERATE" ? `${STATUS.warning.border} bg-amber-50/20` : "border-slate-200"
              }`}
            >
              <div className="p-3 cursor-pointer" onClick={() => setExpandedBN(expandedBN === b.id ? null : b.id)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-[9px] font-black text-slate-400">{b.id}</span>
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${riskBg(b.status)}`}>{t(b.status)}</span>
                      <span className="text-[9px] text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded-full">ETA: {t(b.eta)}</span>
                    </div>
                    <p className="text-xs font-bold text-slate-800">{t(b.location)}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{t(b.action)}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-base font-black" style={{ color: statusHex(thresholdStatus(b.risk, [
                        { min: 8, bucket: STATUS_KEY.critical },
                        { min: 6, bucket: STATUS_KEY.warning },
                        { min: 0, bucket: STATUS_KEY.good },
                      ])) }}>
                        {b.risk}
                      </div>
                      <div className="text-[9px] text-slate-400">{t("risk")}</div>
                    </div>
                    {expandedBN === b.id ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
                  </div>
                </div>
              </div>
              <AnimatePresence>
                {expandedBN === b.id && (
                  <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                    exit={{ height:0, opacity:0 }} transition={{ duration:0.18 }}
                    className="overflow-hidden border-t border-slate-200"
                  >
                    <div className="p-3 bg-slate-50 rounded-b-xl grid grid-cols-2 sm:grid-cols-3 gap-3 text-center text-xs">
                      <div>
                        <div className="text-base font-black text-red-600">{b.density}/m²</div>
                        <div className="text-[9px] text-slate-500">{t("Crowd Density")}</div>
                      </div>
                      <div>
                        <div className="text-base font-black text-orange-600">{b.velocity} m/min</div>
                        <div className="text-[9px] text-slate-500">{t("Flow Velocity")}</div>
                      </div>
                      <div>
                        <div className="text-base font-black text-amber-600">{b.risk}/10</div>
                        <div className="text-[9px] text-slate-500">{t("Risk Score")}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </ChartCard>

      {/* Entry / Exit Gate Flow */}
      <ChartCard title="Entry / Exit Gate Flow Monitor"
        subtitle="Per-gate inflow, outflow and net crowd balance · gates with positive net require attention">
        <div className="overflow-x-auto">
          <table className="gov-table">
            <thead>
              <tr>
                <th>{t("Gate")}</th><th>{t("Entry / hr")}</th><th>{t("Exit / hr")}</th><th>{t("Net Flow")}</th><th>{t("Load")}</th><th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {GATE_FLOW.map((g, i) => (
                <motion.tr key={g.gate} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td className="font-bold text-slate-800">{t(g.gate)}</td>
                  <td className="text-blue-700 font-semibold">{g.entry.toLocaleString()}</td>
                  <td className="text-emerald-700 font-semibold">{g.exit.toLocaleString()}</td>
                  <td>
                    <span className={`flex items-center gap-1 font-black text-xs ${
                      g.net > 0 ? STATUS.critical.text : STATUS.good.text
                    }`}>
                      {g.net > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                      {g.net > 0 ? `+${g.net.toLocaleString()}` : g.net.toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full" style={{
                          width:`${Math.round((g.entry/g.capacity)*100)}%`,
                          background: riskColor(g.status)
                        }} />
                      </div>
                      <span className="text-[10px] font-bold">{Math.round((g.entry/g.capacity)*100)}%</span>
                    </div>
                  </td>
                  <td>
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${riskBg(g.status)}`}>
                      {t(g.status.toUpperCase())}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Entry vs Exit bar chart */}
        <div className="mt-4">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={GATE_FLOW} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="gate" tick={{ ...axisTick, fontSize:7 }} tickFormatter={v => t(v)} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="entry" fill={chartColor(0)} radius={[3,3,0,0]} name={t("Entry/hr")} />
              <Bar dataKey="exit"  fill={chartColor(2)} radius={[3,3,0,0]} name={t("Exit/hr")} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Crowd Velocity + Zone Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Crowd Flow Velocity Monitor"
          subtitle="Average movement speed (m/min) per zone — low velocity = compression risk" live>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={VELOCITY_DATA} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} unit=" m/m" domain={[0, 2.2]} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="ramkund"   stroke={chartColor(0)} strokeWidth={2.5} dot={{ r:2 }} name={t("Ramkund")} />
              <Line type="monotone" dataKey="panchvati" stroke={chartColor(1)} strokeWidth={2}   dot={{ r:2 }} name={t("Panchvati")} />
              <Line type="monotone" dataKey="trimbak"   stroke={chartColor(3)} strokeWidth={2}   dot={{ r:2 }} name={t("Trimbak")} />
              <Line type="monotone" dataKey="nashikRd"  stroke={chartColor(2)} strokeWidth={2}   dot={{ r:2 }} name={t("Nashik Rd")} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-[10px] text-red-700 font-semibold">
              <AlertTriangle size={10} className="inline mr-1" />
              {t("Ramkund 0.4 m/min — critically slow flow. Stampede risk threshold is <0.5 m/min.")}
            </p>
          </div>
        </ChartCard>

        <ChartCard title="Multi-Dimensional Zone Risk Radar"
          subtitle="Density · velocity · pressure · turbulence · exit flow per selected zone">
          {/* Zone selector */}
          <div className="flex flex-wrap gap-1 mb-3">
            {ZONE_RADAR.map((z, i) => (
              <button key={z.zone} onClick={() => setRadarZone(i)}
                className={`text-[9px] font-black px-2.5 py-1 rounded-lg transition-colors ${
                  radarZone === i ? "gov-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>{t(z.zone)}</button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={[
              { axis:"Density",    val: ZONE_RADAR[radarZone].density    },
              { axis:"Velocity",   val: 100 - ZONE_RADAR[radarZone].velocity },
              { axis:"Pressure",   val: ZONE_RADAR[radarZone].pressure   },
              { axis:"Turbulence", val: ZONE_RADAR[radarZone].turbulence },
              { axis:"Exit Gap",   val: 100 - ZONE_RADAR[radarZone].exitFlow },
            ]} margin={{ top:10 }}>
              <PolarGrid stroke={gridProps.stroke} />
              <PolarAngleAxis dataKey="axis" tick={axisTick} tickFormatter={v => t(v)} />
              <PolarRadiusAxis angle={90} domain={[0,100]} tick={{ ...axisTick, fontSize:8 }} />
              <Radar dataKey="val" stroke={chartColor(0)} fill={chartColor(0)} fillOpacity={0.25} name={t("Risk Score")} />
              <Tooltip content={<ChartTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="text-center text-[10px] text-slate-500 mt-1">
            {t("Higher = higher risk in that dimension ·")} <span className="font-bold text-blue-700">{t(ZONE_RADAR[radarZone].zone)}</span>
          </div>
        </ChartCard>
      </div>

      {/* Zone × Hour Heatmap */}
      <ChartCard title="Crowd Density Heatmap — Zone × Hour"
        subtitle="Load % per zone per hour · red = critical overload · green = safe capacity">
        <div className="overflow-x-auto">
          <table className="gov-table">
            <thead>
              <tr>
                <th>{t("Hour")}</th>
                {["Ramkund","Panchvati","Trimbak","Nashik Rd","Kalaram","Gangapur"].map(z => (
                  <th key={z}>{t(z)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {crowdHeatmapHours.map((row, i) => (
                <motion.tr key={row.hour} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
                  <td className="font-bold text-slate-700">{row.hour}</td>
                  {[row.ramkund,row.panchvati,row.trimbak,row.nashikRd,row.kalaram,row.gangapur].map((v, j) => (
                    <td key={j} className="text-center p-0">
                      <div className={`mx-1 my-1 rounded-lg py-2 text-[11px] font-black ${statusClasses(thresholdStatus(v, [
                        { min: 85, bucket: STATUS_KEY.critical },
                        { min: 70, bucket: STATUS_KEY.warning },
                        { min: 50, bucket: STATUS_KEY.info },
                        { min: 0,  bucket: STATUS_KEY.good },
                      ]))}`}>
                        {v}%
                      </div>
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Route Saturation + Historical Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Route Saturation — Entry / Exit Corridors"
          subtitle="Crowd load on key pilgrimage corridors · evacuation pressure">
          <div className="space-y-3.5">
            {routeSaturation.map((r, i) => (
              <motion.div key={r.route}
                initial={{ opacity:0, x:-6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700 truncate flex-1 mr-2">{t(r.route)}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-xs font-black text-slate-900">{r.load}%</span>
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${riskBg(r.status)}`}>
                      {t(r.status.toUpperCase())}
                    </span>
                  </div>
                </div>
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-3 rounded-full"
                    initial={{ width:0 }} animate={{ width:`${r.load}%` }}
                    transition={{ duration:0.8, delay: i * 0.07 }}
                    style={{ background: riskColor(r.status) }}
                  />
                </div>
                {r.load >= 80 && (
                  <p className="text-[9px] text-red-600 font-bold mt-0.5 flex items-center gap-1">
                    <AlertTriangle size={8} /> {t("Consider alternate route advisory")}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Historical Peak Comparison"
          subtitle="Today vs previous Kumbh events — crowd peak and incident count">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={HISTORICAL} layout="vertical" margin={{ left:10, right:60 }}>
              <XAxis type="number" tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
              <YAxis type="category" dataKey="event" tick={{ ...axisTick, fontSize:8 }} tickFormatter={v => t(v)} width={140} />
              <Tooltip content={<ChartTooltip formatter={(v, n) => [v.toLocaleString(), n]} />} />
              <Bar dataKey="peak" radius={[0,4,4,0]} name={t("Peak Crowd")}>
                {HISTORICAL.map((h, i) => (
                  <Cell key={i} fill={h.event.includes("Today") ? chartColor(0) : "#94A3B8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-[10px] text-emerald-800 font-semibold">
              <CheckCircle size={10} className="inline mr-1" />
              {t("Today's peak estimate (8,10,000) is 26% lower than 2015 Snan 1 peak — improved crowd management effectiveness.")}
            </p>
          </div>
        </ChartCard>
      </div>

      {/* Crowd Composition + AI Interventions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Crowd Composition Analysis"
          subtitle="Pilgrim demographic mix — informs medical, mobility and accessibility planning">
          <div className="flex items-center gap-6">
            <ResponsiveContainer width="45%" height={200}>
              <PieChart>
                <Pie data={CROWD_COMPOSITION.map(c => ({ ...c, name: t(c.name) }))} dataKey="value" cx="50%" cy="50%"
                  innerRadius={50} outerRadius={85}
                  label={({ value }) => `${value}%`} labelLine={false}>
                  {CROWD_COMPOSITION.map((c, i) => <Cell key={i} fill={chartColor(i)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip formatter={(v, n) => [`${v}%`, n]} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 flex-1">
              {CROWD_COMPOSITION.map((c, i) => (
                <div key={c.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                    <span className="text-xs text-slate-700">{t(c.name)}</span>
                  </div>
                  <span className="text-sm font-black text-slate-800">{c.value}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-[10px] text-blue-800 font-semibold">
              {t("18% senior citizens + 4% differently abled = high vulnerability cohort. Priority lanes and wheelchair routes activated.")}
            </p>
          </div>
        </ChartCard>

        <ChartCard title="AI Crowd Intervention Recommendations"
          subtitle="AI-generated actions ranked by priority · human approval required for execution">
          <div className="space-y-2">
            {AI_INTERVENTIONS.map((a, i) => (
              <motion.div key={i}
                initial={{ opacity:0, x:6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className={`p-3 rounded-xl border ${
                  a.priority === "P1" ? `${STATUS.critical.border} bg-red-50/30` :
                  a.priority === "P2" ? `${STATUS.warning.border} bg-amber-50/20` : "border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1">
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${
                      a.priority === "P1" ? `${STATUS.critical.bg} ${STATUS.critical.text}` :
                      a.priority === "P2" ? `${STATUS.warning.bg} ${STATUS.warning.text}` : `${STATUS.info.bg} ${STATUS.info.text}`
                    }`}>{a.priority}</span>
                    <div>
                      <p className="text-xs font-semibold text-slate-800 leading-snug">{t(a.action)}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[9px] text-slate-500">
                        <span>{t(a.zone)}</span>
                        <span>·</span>
                        <span>{a.dept}</span>
                        <span>·</span>
                        <span className="text-blue-600 font-bold">AI: {a.confidence}%</span>
                      </div>
                    </div>
                  </div>
                  <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                    a.status === "Active"      ? `${STATUS.good.bg} ${STATUS.good.text}` :
                    a.status === "In Progress" ? `${STATUS.info.bg} ${STATUS.info.text}` : `${STATUS.warning.bg} ${STATUS.warning.text}`
                  }`}>{t(a.status)}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-xl text-[10px] text-blue-700 font-semibold">
            {t("Human-in-loop: All P1/P2 actions require officer approval before execution. AI cannot autonomously enforce.")}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
